/* Editor — Generation Progress View (2026-10).
 *
 * Live visibility into every background Loom novel-build job
 * (`novel_build_jobs`) without opening each manuscript — polls
 * `GET /editor/generation-progress` and groups jobs by state so an
 * editor can see what's actively running, what stalled (likely an
 * orphaned worker from a backend hot-reload — recoverable via
 * Batch Draft-All), and what recently finished.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Play, AlertTriangle, CheckCircle2, Ban, Clock } from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const STATUS_META = {
  running:   { label: 'Running',   icon: Loader2,       className: 'bg-sky-500/15 text-sky-600',      spin: true },
  queued:    { label: 'Queued',    icon: Clock,         className: 'bg-amber-500/15 text-amber-600' },
  paused:    { label: 'Paused',    icon: Clock,         className: 'bg-amber-500/15 text-amber-600' },
  stalled:   { label: 'Stalled',   icon: AlertTriangle, className: 'bg-destructive/15 text-destructive' },
  error:     { label: 'Error',     icon: AlertTriangle, className: 'bg-destructive/15 text-destructive' },
  done:      { label: 'Done',      icon: CheckCircle2,  className: 'bg-emerald-500/15 text-emerald-600' },
  cancelled: { label: 'Cancelled', icon: Ban,           className: 'bg-muted text-muted-foreground' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function JobRow({ job }) {
  const meta = STATUS_META[job.status] || STATUS_META.queued;
  const Icon = meta.icon;
  // A "done" job only ever drafted the chapters it was dispatched for
  // (a re-run after a partial stall covers just the remaining range),
  // so `progress_pct` (done chapters / full novel target) understates
  // a finished job — display 100% once status says it's actually done.
  const pct = job.status === 'done' ? 100 : (job.progress_pct || 0);
  return (
    <div data-testid={`gen-progress-job-${job.job_id}`} className="rounded-md border border-border p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-[12px] font-semibold">{job.title}</span>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium ${meta.className}`}>
          <Icon className={`h-3 w-3 ${meta.spin ? 'animate-spin' : ''}`} /> {meta.label}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className={`h-full rounded-full ${job.status === 'error' || job.status === 'stalled' ? 'bg-destructive/60' : job.status === 'done' ? 'bg-emerald-500' : 'bg-primary'}`}
          style={{ width: `${Math.min(100, pct)}%` }}
          data-testid={`gen-progress-bar-${job.job_id}`}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[10.5px] text-muted-foreground">
        <span>
          Ch. {job.current_chapter ?? 0}/{job.total_chapters} · {pct}% · {(job.word_count || 0).toLocaleString()} words
        </span>
        <span>{timeAgo(job.updated_at)}</span>
      </div>
      {job.error && (
        <div className="mt-1 truncate text-[10.5px] text-destructive" data-testid={`gen-progress-error-${job.job_id}`}>
          {job.error}
        </div>
      )}
    </div>
  );
}

const SECTIONS = [
  { key: 'active',   label: 'Active',            statuses: ['running', 'queued', 'paused'] },
  { key: 'attention', label: 'Needs attention',  statuses: ['stalled', 'error'] },
  { key: 'finished', label: 'Recently finished', statuses: ['done', 'cancelled'] },
];

export default function EditorGenerationProgressPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await http.get('/editor/generation-progress');
      setJobs(r.data?.jobs || []);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Failed to load generation progress.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => load(true), 10000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const recoverStalled = async () => {
    setRecovering(true);
    try {
      const r = await http.post('/loom/manuscripts/draft-all', {});
      const n = r.data?.orphan_recovered || 0;
      const d = r.data?.dispatched || 0;
      toast.success(`Recovered ${n} stalled manuscript(s), dispatched ${d} job(s).`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery failed.');
    } finally { setRecovering(false); }
  };

  const byStatus = (statuses) => jobs.filter((j) => statuses.includes(j.status));
  const stalledCount = byStatus(['stalled', 'error']).length;

  return (
    <section data-testid="editor-generation-progress-panel" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[13px] font-semibold">Generation Progress</div>
          <div className="text-[10.5px] text-muted-foreground">
            Live status of background novel-build jobs — refreshes every 10s.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stalledCount > 0 && (
            <button
              type="button"
              onClick={recoverStalled}
              disabled={recovering}
              data-testid="gen-progress-recover-stalled-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-2.5 py-1 text-[11px] font-medium text-destructive hover:bg-destructive/10 disabled:opacity-60"
            >
              {recovering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Recover {stalledCount} stalled
            </button>
          )}
          <button
            type="button"
            onClick={() => load()}
            data-testid="gen-progress-refresh-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading job progress…
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground" data-testid="gen-progress-empty">
          No generation jobs yet — dispatch manuscripts from All Manuscripts or Batch Draft-All.
        </div>
      ) : (
        SECTIONS.map(({ key, label, statuses }) => {
          const rows = byStatus(statuses);
          if (rows.length === 0) return null;
          return (
            <div key={key} data-testid={`gen-progress-section-${key}`} className="space-y-1.5">
              <div className="text-[11px] font-semibold text-muted-foreground">{label} ({rows.length})</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {rows.map((j) => <JobRow key={j.job_id} job={j} />)}
              </div>
            </div>
          );
        })
      )}
    </section>
  );
}
